/**
 * notificationsSlice — in-app UI toast queue metadata.
 *
 * Manages the queue of in-app toast/notification entries. This is purely
 * display/UI state — it is NOT the server notification inbox.
 *
 * Rules:
 * - Toast messages must be plain translated UI strings. No guest PII, no transcript
 *   content, no raw error payloads. If an error needs to be shown, show a localized
 *   message; log the raw error via packages/observability only.
 * - IDs are generated by the caller (e.g. crypto.randomUUID() or a nanoid).
 * - Toasts are auto-removed by the UI layer after their duration; dismiss() can be
 *   called early. The Redux state only holds entries until dismissed.
 * - Max MAX_TOASTS entries kept to prevent unbounded growth.
 */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

const MAX_TOASTS = 5;

export type NotificationVariant = 'info' | 'success' | 'warning' | 'error';

export interface NotificationEntry {
  /** Caller-supplied stable ID (e.g. crypto.randomUUID()). */
  id: string;
  /** Variant controls icon and color scheme in the toast component. */
  variant: NotificationVariant;
  /**
   * Localized title string — must be a translated UI message, not raw server text.
   * Must NOT contain guest PII, emails, phone numbers, or transcript content.
   */
  title: string;
  /**
   * Optional localized description. Same PII rules as title.
   */
  description?: string;
  /** Duration in milliseconds before the UI auto-dismisses. Undefined = sticky. */
  durationMs?: number;
}

export interface NotificationsState {
  /** Ordered queue of active toast entries (oldest first). */
  toasts: NotificationEntry[];
}

const initialState: NotificationsState = {
  toasts: [],
};

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    /**
     * Enqueue a new toast notification.
     * If the queue is at MAX_TOASTS, the oldest entry is dropped to make room.
     */
    addNotification(state, action: PayloadAction<NotificationEntry>) {
      if (state.toasts.length >= MAX_TOASTS) {
        state.toasts.shift();
      }
      state.toasts.push(action.payload);
    },
    /** Remove a specific toast by ID (explicit dismiss or timer expiry). */
    dismissNotification(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },
    /** Remove all toasts (e.g. on route transition or logout). */
    clearAllNotifications(state) {
      state.toasts = [];
    },
  },
});

export const { addNotification, dismissNotification, clearAllNotifications } =
  notificationsSlice.actions;

// Selectors
export const selectToasts = (state: { notifications: NotificationsState }) =>
  state.notifications.toasts;

export const selectToastById =
  (id: string) =>
  (state: { notifications: NotificationsState }): NotificationEntry | undefined =>
    state.notifications.toasts.find((t) => t.id === id);
